import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { ConditionMonitorMetadataOutputV1 } from '@seeqdev/api/model/ConditionMonitorMetadataOutputV1';

/**
 * In-memory store for condition monitors. Like `itemRuntime.store.ts` this is runtime-only and must
 * never be persisted, but it is condition-specific rather than shared across item types, so it
 * lives in its own store.
 */

export interface ConditionMonitors {
  /** Ids of the condition monitors notifying on this condition; empty when none do */
  notifications: string[];
  /** Ids of the vantage condition monitors watching this condition; empty when none do */
  vantages: string[];
}

export interface ConditionRuntimeState {
  /** Monitors keyed by condition id. An entry exists for every condition that has been queried, so
   * an entry with two empty arrays means "asked, and nothing monitors it"; no entry means "never asked". */
  monitors: Record<string, ConditionMonitors>;
  /**
   * The monitors themselves, from the most recent fetch. Replaced wholesale, and callers fetch with
   * differently scoped condition id sets, so this is the last query's result rather than a complete
   * picture.
   */
  metadataList: ConditionMonitorMetadataOutputV1[];
}

export interface ConditionRuntimeActions {
  /**
   * Records which monitors watch each of the given conditions, splitting them into vantage and
   * notification monitors. Conditions in `conditionIds` with no monitor get an empty entry, so a
   * condition that stops being monitored is recorded as such rather than keeping its previous ids.
   */
  setMonitors(payload: {
    conditionIds: string[];
    conditionMonitorMetadataList: ConditionMonitorMetadataOutputV1[];
  }): void;
  /** Discards a condition's monitors, for when the condition no longer exists */
  clearValues(id: string): void;
  reset(): void;
}

export const initialState: ConditionRuntimeState = Object.freeze({
  monitors: {},
  metadataList: [],
});

const STORE_NAME = 'ConditionRuntime';

const conditionRuntimeActionsFactory: ActionsFactory<ConditionRuntimeState, ConditionRuntimeActions> = (
  set,
  get,
  resetState,
) => ({
  setMonitors: ({ conditionIds, conditionMonitorMetadataList }) => {
    const monitors: Record<string, ConditionMonitors> = {};
    conditionIds.forEach((conditionId) => {
      monitors[conditionId] = { notifications: [], vantages: [] };
    });

    conditionMonitorMetadataList.forEach((metadata) => {
      metadata.conditionIds.forEach((conditionId) => {
        const entry = monitors[conditionId];
        if (!entry) {
          return;
        }
        metadata.workbook?.type === SeeqNames.Types.Vantage
          ? entry.vantages.push(metadata.id)
          : entry.notifications.push(metadata.id);
      });
    });

    const current = get();
    const merged = { ...current.monitors, ...monitors };
    if (_.isEqual(current.monitors, merged) && _.isEqual(current.metadataList, conditionMonitorMetadataList)) {
      return;
    }
    set((draft) => {
      draft.monitors = merged;
      draft.metadataList = conditionMonitorMetadataList;
    });
  },

  clearValues: (id) => {
    if (!(id in get().monitors)) {
      return;
    }
    set((draft) => {
      delete draft.monitors[id];
    });
  },

  reset: resetState,
});

const [ConditionRuntimeStoreProvider, useConditionRuntimeStoreInstance, useConditionRuntimeSelector] = createStore(
  STORE_NAME,
  initialState,
  conditionRuntimeActionsFactory,
);

export { ConditionRuntimeStoreProvider };

export const useConditionMonitors = () => useConditionRuntimeSelector((state) => state.monitors);

export const useConditionMonitor = (conditionId: string): ConditionMonitors | undefined =>
  useConditionRuntimeSelector((state) => state.monitors[conditionId]);

export const useConditionMonitorMetadataList = () => useConditionRuntimeSelector((state) => state.metadataList);

export const useConditionRuntimeActions = () => useConditionRuntimeStoreInstance().actions;

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. Read state via `getState()` and mutate via `store.actions.*`.
 * New code should use the hooks above and the Provider.
 */
export function legacyCreateConditionRuntimeStore(initialStateOverrides?: Partial<ConditionRuntimeState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    conditionRuntimeActionsFactory,
    { storeName: STORE_NAME },
  );
}
